import React from 'react';

/**
 * Props for TrendStatCard component
 */
export interface TrendStatCardProps {
  /**
   * Stat title/label
   */
  title: string;

  /**
   * Current value
   */
  value: string | number;

  /**
   * Previous value for comparison
   */
  previousValue?: string | number;

  /**
   * Trend percentage (calculated if not provided)
   */
  trendPercentage?: number;

  /**
   * Whether increase is positive (default: true)
   */
  increaseIsPositive?: boolean;

  /**
   * Icon to display
   */
  icon: React.ReactNode;

  /**
   * Optional subtitle or description
   */
  subtitle?: string;

  /**
   * Comparison period text
   */
  comparisonText?: string;

  /**
   * Color variant
   */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';

  /**
   * Loading state
   */
  isLoading?: boolean;

  /**
   * Custom className
   */
  className?: string;
}

/**
 * Calculate percentage change
 */
const calculateChange = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Get variant styles
 */
const getVariantStyles = (variant: TrendStatCardProps['variant'] = 'default') => {
  const styles = {
    default: {
      iconBg: 'bg-gradient-to-br from-gray-500 to-gray-600',
      accentBar: 'bg-gradient-to-r from-gray-400 to-gray-500',
    },
    primary: {
      iconBg: 'bg-gradient-to-br from-primary-500 to-primary-700',
      accentBar: 'bg-gradient-to-r from-primary-400 to-primary-600',
    },
    success: {
      iconBg: 'bg-gradient-to-br from-green-500 to-emerald-600',
      accentBar: 'bg-gradient-to-r from-green-400 to-emerald-500',
    },
    warning: {
      iconBg: 'bg-gradient-to-br from-amber-500 to-orange-600',
      accentBar: 'bg-gradient-to-r from-amber-400 to-orange-500',
    },
    danger: {
      iconBg: 'bg-gradient-to-br from-red-500 to-pink-600',
      accentBar: 'bg-gradient-to-r from-red-400 to-pink-500',
    },
  };
  return styles[variant];
};

/**
 * TrendStatCard Component
 *
 * Enhanced stat card with trend indicators, sparklines, and visual feedback.
 * Shows percentage change with contextual coloring.
 *
 * @example
 * ```tsx
 * <TrendStatCard
 *   title="Active Campaigns"
 *   value={24}
 *   previousValue={20}
 *   icon={<CampaignIcon />}
 *   subtitle="campaigns running"
 *   comparisonText="vs last week"
 *   variant="primary"
 *   increaseIsPositive={true}
 * />
 * ```
 */
export function TrendStatCard({
  title,
  value,
  previousValue,
  trendPercentage: providedTrendPercentage,
  increaseIsPositive = true,
  icon,
  subtitle,
  comparisonText = 'vs previous period',
  variant = 'default',
  isLoading = false,
  className = '',
}: TrendStatCardProps) {
  const styles = getVariantStyles(variant);

  // Calculate trend
  let trendPercentage = providedTrendPercentage;
  if (trendPercentage === undefined && previousValue !== undefined) {
    const currentNum = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    const previousNum = typeof previousValue === 'string' ? parseFloat(previousValue.replace(/,/g, '')) : previousValue;
    trendPercentage = calculateChange(currentNum, previousNum);
  }

  // Determine trend direction and positivity
  const hasTrend = trendPercentage !== undefined && trendPercentage !== 0;
  const trendDirection = hasTrend ? (trendPercentage! > 0 ? 'up' : 'down') : 'neutral';
  const isPositiveTrend = hasTrend
    ? (trendDirection === 'up' && increaseIsPositive) || (trendDirection === 'down' && !increaseIsPositive)
    : null;

  if (isLoading) {
    return (
      <div
        className={`relative overflow-hidden bg-white border border-gray-200/60 rounded-xl p-6 shadow-sm ${className}`}
        data-testid="trend-stat-card-loading"
      >
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-28 mb-3"></div>
              <div className="h-9 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
            </div>
            <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
          </div>
          <div className="h-1 bg-gray-200 rounded-full w-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group relative overflow-hidden bg-white border border-gray-200/60 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105 ${className}`}
      data-testid="trend-stat-card"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-100/50 to-transparent rounded-full blur-2xl -mr-16 -mt-16"></div>

      {/* Accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${styles.accentBar}`}></div>

      {/* Content */}
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            {/* Title */}
            <h3
              className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2"
              data-testid="trend-stat-title"
            >
              {title}
            </h3>

            {/* Value */}
            <div className="flex items-baseline gap-3">
              <span
                className="text-3xl font-bold text-gray-900"
                data-testid="trend-stat-value"
              >
                {value}
              </span>

              {/* Trend Badge */}
              {hasTrend && (
                <div
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                    isPositiveTrend
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                  data-testid="trend-stat-badge"
                >
                  <span className="text-sm">
                    {trendDirection === 'up' ? '↑' : '↓'}
                  </span>
                  <span>{Math.abs(trendPercentage!).toFixed(1)}%</span>
                </div>
              )}
            </div>

            {/* Subtitle or Comparison */}
            {(subtitle || (hasTrend && comparisonText)) && (
              <p className="text-xs text-gray-500 mt-2 font-medium" data-testid="trend-stat-subtitle">
                {subtitle || comparisonText}
              </p>
            )}
          </div>

          {/* Icon */}
          <div
            className={`flex-shrink-0 w-12 h-12 rounded-xl ${styles.iconBg} shadow-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300`}
            data-testid="trend-stat-icon"
          >
            <div className="w-6 h-6">{icon}</div>
          </div>
        </div>

        {/* Mini Progress Bar (visual enhancement) */}
        {hasTrend && (
          <div className="mt-4">
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  isPositiveTrend ? 'bg-green-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(Math.abs(trendPercentage!), 100)}%` }}
                data-testid="trend-stat-progress"
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrendStatCard;
